import { Component, createRef, RefObject } from 'react';
import { ILobbyState } from '../../../../Core/State/ILobbyState';
import { IRoomState } from '../../../../Core/State/IRoomState';
import { IUserState } from '../../../../Core/State/IUserState';
import { DataGrid, GridColDef, GridRowParams } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import RoomInformation from '../../Common/RoomInformation';
import styles from '../../../../assets/css/components/Lobby.module.css';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { createTheme, ThemeProvider, Tooltip } from '@mui/material';
import { t } from '../../../../i18n';
import bnbIcon from '/crypto/bnb.svg';
import busdIcon from '/crypto/busd.svg';
import daiIcon from '/crypto/dai.svg';
import dogeIcon from '/crypto/doge.svg';
import dotIcon from '/crypto/dot.svg';
import shibIcon from '/crypto/shib.svg';
import solIcon from '/crypto/sol.svg';
import solamaIcon from '/crypto/solama.svg';
import usdcIcon from '/crypto/usdc.svg';
import usdtIcon from '/crypto/usdt.svg';
import vlxIcon from '/crypto/vlx.svg';
import xmrIcon from '/crypto/xmr.svg';

interface LobbyProps {
    joinRoom: (roomId: string, seatIndex?: number, sourceWindow?: Window | null) => void;
    userState: IUserState;
    lobbyState: ILobbyState;
    selectedRoomId: string | null;
    sendChatMessage: (message: string) => void;
}

interface LobbyState {
    chatInput: string;
    selectedRoom: IRoomState | null;
    isDragging: boolean;
    lastClientY: number;
}

export default class DesktopTournamentRoomsLobby extends Component<LobbyProps, LobbyState> {
    private dataGridRef: RefObject<HTMLDivElement>;
    private lastClientY: number = 0;
    
    constructor(props: LobbyProps) {
        super(props);
        this.state = {
            chatInput: '',
            selectedRoom: null,
            isDragging: false,
            lastClientY: 0
        };
        this.dataGridRef = createRef<HTMLDivElement>();
    }

    darkTheme = createTheme({
        palette: {
            mode: "dark",
            primary: {
                main: "#F93751",
            },
        },
        typography: {
            fontFamily: 'Inter, serif',
        },
    });

    componentDidUpdate(prevProps: LobbyProps) {
        if (this.state.selectedRoom && prevProps.lobbyState !== this.props.lobbyState) {
            const updatedRoom = this.props.lobbyState.rooms.find(room => room.roomId === this.state.selectedRoom?.roomId);
            if (updatedRoom) {
                this.setState({ selectedRoom: updatedRoom });
            }
        }

        // Update selected room when selectedRoomId changes
        if (prevProps.selectedRoomId !== this.props.selectedRoomId) {
            const selectedRoom = this.props.lobbyState.rooms.find(room => room.roomId === this.props.selectedRoomId);
            this.setState({ selectedRoom: selectedRoom || null });
        }
    }

    handleRowClick = (params: GridRowParams) => {
        this.setState({ selectedRoom: params.row as IRoomState });
    };

    getCryptoIcon = (currency: string) => {
        switch(currency?.toLowerCase()) {
            case 'bnb':
                return bnbIcon;
            case 'busd':
                return busdIcon;
            case 'dai':
                return daiIcon;
            case 'doge':
                return dogeIcon;
            case 'dot':
                return dotIcon;
            case 'shib':
                return shibIcon;
            case 'sol':
                return solIcon;
            case 'solama':
                return solamaIcon;
            case 'usdc':
                return usdcIcon;
            case 'usdt':
                return usdtIcon;
            case 'vlx':
                return vlxIcon;
            case 'xmr':
                return xmrIcon;
            default:
                return null;
        }
    };

    handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        this.lastClientY = e.clientY;
        this.setState({ isDragging: true });
    };

    handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (this.state.isDragging && this.dataGridRef.current) {
            const delta = this.lastClientY - e.clientY;
            const virtualScroller = this.dataGridRef.current.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement;
            if (virtualScroller) {
                virtualScroller.scrollTop += delta;
                this.lastClientY = e.clientY;
            }
        }
    };

    handleMouseUp = () => {
        this.setState({ isDragging: false });
    };

    handleMouseLeave = () => {
        this.setState({ isDragging: false });
    };

    render() {
        const user = this.props.userState;
        
        // Filter rooms that belong to a tournament (assuming a tournamentId exists)
        const filteredRooms = this.props.lobbyState.rooms.filter(room => 
            room.isTournamentTable // Filter by tournamentId presence
        );

        const columns: GridColDef<IRoomState>[] = [
            {
                field: 'name',
                headerName: t('Tournament:columns.table'),
                headerClassName: styles.tableHeader,
                flex: 1,
                type: 'string',
                resizable: false,
                minWidth: 300,
                renderCell: (params) => (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span>{params.value}{params.row.isFinalTournamentTable ? ` ${t('Tournament:labels.final')}` : ''}</span>
                    </div>
                )
            },
            {
                field: 'type',
                headerName: t('Tournament:columns.game'),
                headerClassName: styles.tableHeader,
                flex: 1,
                type: 'string',
                resizable: false,
                minWidth: 90,
                maxWidth: 200,
                valueGetter: (params: any) => {
                    switch (params.value.toLowerCase()) {
                        case 'texas':
                            return t('Tournament:gameTypes.holdem');
                        case 'aof':
                            return t('Tournament:gameTypes.aof');
                        default:
                            return params.value.charAt(0).toUpperCase() + params.value.slice(1);
                    }
                }
            },
            {
                field: 'currency',
                headerName: t('Tournament:columns.currency'),
                headerClassName: styles.tableHeader,
                flex: 1,
                type: 'string',
                resizable: false,
                minWidth: 90,
                maxWidth: 200,
                renderCell: (params) => {
                    const icon = this.getCryptoIcon(params.row.currency);
                    return params.row.currencyType === 'fiat' ? (
                        '$' // Show dollar sign for fiat
                    ) : icon ? (
                        <>
                            <img 
                                src={icon} 
                                alt={params.row.currency} 
                                style={{ width: '20px', height: '20px', marginRight: '6px' }}
                            />
                            <span style={{fontSize:'14px'}}>{params.row.currency}</span>
                        </>
                    ) : params.row.currency;
                }
            },
            {
                field: 'stakes',
                headerName: t('Tournament:columns.stakes'),
                headerClassName: styles.tableHeader,
                flex: 1,
                type: 'string',
                resizable: false,
                minWidth: 125,
                maxWidth: 200,
                valueGetter: (params: any) => params.row.ante ? `${t('Tournament:labels.ante')}: ${params.row.ante}` : `$${params.row.smallBlind} / $${params.row.bigBlind}`,
                renderCell: (params) => {
                    const formatCurrency = (value: number) => {
                        return new Intl.NumberFormat('en-US', {
                            minimumFractionDigits: value % 1 === 0 ? 0 : 2,
                            maximumFractionDigits: 2
                        }).format(value);
                    };

                    const stakesText = params.row.ante ? `${t('Tournament:labels.ante')}: ${formatCurrency(params.row.ante)}` : `${formatCurrency(params.row.smallBlind)} / ${formatCurrency(params.row.bigBlind)}`;
                    
                    if (params.row.currencyType === 'fiat') {
                        return `$${stakesText}`; // Prepend $ for fiat
                    }
                    
                    const cryptoIcon = this.getCryptoIcon(params.row.currency);
                    return (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span>{stakesText}</span>
                            {cryptoIcon && (
                                <Tooltip title={params.row.currency} placement="top" arrow>
                                    <img 
                                        src={cryptoIcon} 
                                        alt={params.row.currency} 
                                        style={{ width: '16px', height: '16px', marginLeft: '6px' }}
                                    />
                                </Tooltip>
                            )}
                        </div>
                    );
                }
            },
            {
                field: 'buyIn',
                headerName: t('Tournament:columns.buyIn'),
                headerClassName: styles.tableHeader,
                flex: 1,
                type: 'string',
                resizable: false,
                minWidth: 125,
                maxWidth: 200,
                valueGetter: (params: any) => params.row.maxBuyIn ? params.row.maxBuyIn : `${params.row.minBuyIn} / ${params.row.maxBuyIn}`,
                renderCell: (params: any) => {
                    const formatCurrency = (value: number) => {
                        return new Intl.NumberFormat('en-US', {
                            minimumFractionDigits: value % 1 === 0 ? 0 : 2,
                            maximumFractionDigits: 2
                        }).format(value);
                    };

                    let buyInText;
                    if (params.row.type === 'aof') {
                        buyInText = formatCurrency(params.row.maxBuyIn);
                    } else if (params.row.type === 'texas') {
                        buyInText = `${formatCurrency(params.row.minBuyIn)} / ${formatCurrency(params.row.maxBuyIn)}`;
                    } else {
                        buyInText = t('Tournament:labels.na');
                    }

                    if (params.row.currencyType === 'fiat') {
                        return `$${buyInText}`; // Prepend $ for fiat
                    } else {
                        const cryptoIcon = this.getCryptoIcon(params.row.currency);
                        return (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span>{buyInText}</span>
                                {cryptoIcon && (
                                    <Tooltip title={params.row.currency} placement="top" arrow>
                                        <img 
                                            src={cryptoIcon} 
                                            alt={params.row.currency} 
                                            style={{ width: '16px', height: '16px', marginLeft: '6px' }}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                        );
                    }
                }
            },
            {
                field: 'seats',
                headerName: t('Tournament:columns.players'),
                headerClassName: styles.tableHeader,
                flex: 1,
                type: 'number',
                resizable: false,
                minWidth: 90,
                maxWidth: 200,
                valueGetter: (params: any) => params.row.playerSatCount,
                align: 'left',
                headerAlign: 'left',
                renderCell: (params) => {
                    const { playerSatCount, minPlayers, maxPlayers } = params.row;

                    let textColor = '#179F55';
                    let tooltip = t('Tournament:tooltips.openSeats');

                    if (playerSatCount < minPlayers) {
                        textColor = '#FFC100';
                        tooltip = t('Tournament:tooltips.needsPlayers', { count: minPlayers });
                    } else if (playerSatCount === maxPlayers) {
                        textColor = '#FF004D';
                        tooltip = t('Tournament:tooltips.tableFull');
                    }
                
                    return (
                        <Tooltip title={tooltip} placement="top" arrow>
                            <span style={{ color: textColor }}>
                                {`${Math.min(params.row.playerSatCount, params.row.maxPlayers)} / ${params.row.maxPlayers}`}
                            </span>
                        </Tooltip>
                    );
                }
            },
            {
                field: 'join',
                headerName: '',
                headerClassName: styles.tableHeader,
                cellClassName: styles.lastTableCell,
                flex: 1,
                type: 'actions',
                resizable: false,
                minWidth: 125,
                maxWidth: 250,
                renderCell: (params: any) => (
                    <Button
                        variant="contained"
                        fullWidth
                        style={{ textTransform: "capitalize" }}
                        onClick={(e) => {
                            const sourceWindow =
                                (e.currentTarget as any)?.ownerDocument?.defaultView ||
                                ((e.nativeEvent as any)?.view as (Window | null | undefined));
                            this.props.joinRoom(params.row.roomId, undefined, sourceWindow);
                        }}
                    >
                        {t('Tournament:actions.openTable')}
                    </Button>
                ),
                align: 'left',
                headerAlign: 'left',
            },
        ];

        const customDataGridClasses = {
            virtualScroller: styles.customScroller,
            columnHeaders: styles.tableHeaderRow,
            columnHeader: styles.tableHeaderCell,
            cell: styles.tableCell
        };

        return (
            <ThemeProvider theme={this.darkTheme}>
                <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100dvh - 130px)', width: '100%', marginTop: '20px' }}>
                    <div style={{ display: 'flex', flexGrow: 1, height: '100%' }}>
                        <div 
                            style={{ flex: '0 0 calc(100% - 360px)', marginRight: '20px', backgroundColor: '#2a2b32' }}
                            ref={this.dataGridRef}
                            onMouseDown={this.handleMouseDown}
                            onMouseMove={this.handleMouseMove}
                            onMouseUp={this.handleMouseUp}
                            onMouseLeave={this.handleMouseLeave}
                        >
                            <DataGrid
                                disableColumnMenu
                                disableColumnFilter
                                getCellClassName={(params) => styles.tableCell}
                                getRowClassName={(params) => {
                                    const userId = user?.id;
                                    if (!userId) {
                                        return styles.tableRow;
                                    }
                                    // Check players array for the user's ID
                                    const isUserPlaying = params.row.players?.some((player: { userId: string }) => player.userId === user.id);
                                    return isUserPlaying ? styles.active : styles.tableRow;
                                }}
                                rows={filteredRooms}
                                columns={columns}
                                classes={customDataGridClasses}
                                pageSizeOptions={[500]}
                                getRowId={(row) => row.roomId}
                                hideFooter
                                onRowClick={this.handleRowClick}
                                slots={{
                                    columnSortedDescendingIcon: ArrowDropUp,
                                    columnSortedAscendingIcon: ArrowDropDown,
                                }}
                                initialState={{
                                    sorting: {
                                        sortModel: [{ field: 'seats', sort: 'desc' }] // Default sort by player count
                                    }
                                }}
                                sx={{
                                    border: 'none',
                                    color: 'white',
                                    height: '100%',
                                    cursor: this.state.isDragging ? 'grabbing' : 'grab',
                                    '& .MuiDataGrid-virtualScroller': {
                                        '&::-webkit-scrollbar': {
                                            width: '0.4em',
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: 'transparent',
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            backgroundColor: 'rgba(255,255,255,.3)',
                                        },
                                        '&::-webkit-scrollbar-thumb:hover': {
                                            background: 'rgba(255,255,255,.5)',
                                        },
                                    },
                                    '& .MuiDataGrid-root': {
                                        fontFamily: 'Inter, serif'
                                    },
                                    '& .MuiDataGrid-columnHeader': {
                                        fontFamily: 'Inter, serif'
                                    },
                                    '& .MuiDataGrid-cell': {
                                        fontFamily: 'Inter, serif'
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                        cursor: 'pointer',
                                        backgroundColor: '#3A3D50 !important',
                                    },
                                    '& .MuiDataGrid-row:focus': {
                                        backgroundColor: '#3A3D50',
                                    },
                                    '& .MuiDataGrid-row:active': {
                                        backgroundColor: '#3A3D50',
                                    },
                                    '& .MuiDataGrid-sortIcon': {
                                        color: '#66666b',
                                        fontSize: '2rem'
                                    },
                                    '& .MuiDataGrid-row.Mui-selected': {
                                        backgroundColor: '#3A3D50',
                                    },
                                    '& .MuiDataGrid-columnSeparator': {
                                        display: 'none !important',
                                    },
                                }}
                            />
                        </div>
                        <div style={{ flex: '0 0 340px', display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <RoomInformation
                                selectedRoom={this.state.selectedRoom}
                                joinRoom={(roomId: string) => this.props.joinRoom(roomId)}
                                userState={this.props.userState}
                            />
                        </div>
                    </div>
                </div>
            </ThemeProvider>
        );
    }
} 
